import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageMeta from '@/components/base/PageMeta';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { getComicsByCategory, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');

  const categoryName = useMemo(() => {
    const cat = categories.find((c) => c.slug === currentSlug);
    return cat ? cat.name : '全部';
  }, [currentSlug]);

  const filteredComics = useMemo(() => {
    const comics = getComicsByCategory(currentSlug);
    switch (sortBy) {
      case 'latest':
        return [...comics].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      case 'rating':
        return [...comics].sort((a, b) => b.rating - a.rating);
      default:
        return [...comics].sort((a, b) => b.views - a.views);
    }
  }, [currentSlug, sortBy]);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 韩国漫画分类 | 尾巴漫画`,
    description: `浏览${categoryName}类型的韩国漫画，无删减免费在线阅读。尾巴漫画为您精选优质韩漫资源。`,
    url: typeof window !== 'undefined' ? `${window.location.origin}/category/${currentSlug}` : '',
  };

  return (
    <>
      <PageMeta
        title={`${categoryName} - 韩国漫画分类 | 尾巴漫画`}
        description={`浏览${categoryName}类型的韩国漫画，无删减免费在线阅读。尾巴漫画为您精选优质韩漫资源，下拉式阅读体验。`}
        keywords={`韩国漫画,${categoryName}漫画,无删减漫画,下拉式韩漫,免费韩漫,尾巴漫画`}
        canonicalUrl={`/category/${currentSlug}`}
        jsonLd={jsonLd}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '分类浏览', href: '/category/all' },
                { label: categoryName },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mt-2">{categoryName} 漫画</h1>
            <p className="text-sm text-foreground-500 mt-1">共 {filteredComics.length} 部漫画</p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                  currentSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-6">
            <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
            <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
              {[
                { key: 'popular' as const, label: '最受欢迎' },
                { key: 'latest' as const, label: '最近更新' },
                { key: 'rating' as const, label: '评分最高' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-background-50 text-foreground-950'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-background-100">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400 w-8 h-8 flex items-center justify-center"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无漫画</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}